// src/api/user-service/userService.js
import client from '../client';

// ==================== User Profile ====================

/**
 * Provision user after first login (signup)
 * @param {Object} payload - { roleCode?: 'USER' | 'FARMER' }
 * @returns {Promise<Object>} User profile data
 */
export const provisionUser = async (payload = {}) => {
  return client.post('/users/provision', payload).then(res => res.data);
};

/**
 * Get current user profile
 * @returns {Promise<Object>} User profile data
 */
export const getMyProfile = async () => {
  return client.get('/users/me').then(res => res.data);
};

/**
 * Update current user profile
 * @param {Object} data - { firstName, lastName, phoneNumber }
 * @returns {Promise<Object>} Updated user profile
 */
export const updateMyProfile = async (data) => {
  return client.patch('/users/me', data).then(res => res.data);
};

/**
 * Delete current user account
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteMyAccount = async () => {
  return client.delete('/users/me').then(res => res.data);
};

// ==================== Profile Image ====================

/**
 * Upload profile image
 * @param {File} file - Image file to upload
 * @returns {Promise<Object>} Updated user profile with new image URL
 */
export const uploadProfileImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return client.patch('/users/me/profile-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(res => res.data);
};

/**
 * Remove profile image
 * @returns {Promise<Object>} Updated user profile without image
 */
export const removeProfileImage = async () => {
  return client.delete('/users/me/profile-image').then(res => res.data);
};

// ==================== Points ====================

/**
 * Add points to current user
 * @param {number} points - Number of points to add
 * @returns {Promise<Object>} Updated user profile with new points
 */
export const addMyPoints = async (points) => {
  return client.put(`/users/me/points/add?points=${points}`).then(res => res.data);
};

/**
 * Spend points from current user
 * @param {number} points - Number of points to spend
 * @returns {Promise<Object>} Updated user profile with remaining points
 */
export const spendMyPoints = async (points) => {
  return client.put(`/users/me/points/spend?points=${points}`).then(res => res.data);
};

// ==================== Admin - User Management ====================

/**
 * Get all users (Admin only)
 * @param {Object} query - { page, limit, role, search }
 * @returns {Promise<Object>} List of users
 */
export const getAllUsers = async (query = {}) => {
  return client.get('/users', { params: query }).then(res => res.data);
};

/**
 * Get paginated users (Admin only)
 * @param {Object} query - { page, limit, sort, order, filters }
 * @returns {Promise<Object>} Paginated user list
 */
export const getPaginatedUsers = async (query = {}) => {
  return client.get('/users/paginated', { params: query }).then(res => res.data);
};

/**
 * Get user by ID (Admin only)
 * @param {number} userId - User ID
 * @returns {Promise<Object>} User data
 */
export const getUserById = async (userId) => {
  return client.get(`/users/${userId}`).then(res => res.data);
};

/**
 * Get user by email (Admin only)
 * @param {string} email - User email
 * @returns {Promise<Object>} User data
 */
export const getUserByEmail = async (email) => {
  return client.get('/users/by-email', { params: { email } }).then(res => res.data);
};

/**
 * Get multiple users by IDs (Admin only)
 * @param {Array<number>} userIds - Array of user IDs
 * @returns {Promise<Array>} Array of user data
 */
export const getUsersByIds = async (userIds) => {
  return client.post('/users/bulk', { userIds }).then(res => res.data);
};

/**
 * Update user by ID (Admin only)
 * @param {number} userId - User ID
 * @param {Object} data - User data to update
 * @returns {Promise<Object>} Updated user data
 */
export const updateUserById = async (userId, data) => {
  return client.patch(`/users/${userId}`, data).then(res => res.data);
};

/**
 * Update user role (Admin only)
 * @param {number} userId - User ID
 * @param {Array<number>} roleIds - Array of role IDs [1: USER, 2: FARMER, 3: ADMIN]
 * @returns {Promise<Object>} Updated user data
 */
export const updateUserRole = async (userId, roleIds) => {
  return client.put(`/users/${userId}/roles`, { roleIds }).then(res => res.data);
};

/**
 * Delete user by ID (Admin only)
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteUserById = async (userId) => {
  return client.delete(`/users/${userId}`).then(res => res.data);
};

/**
 * Add points to user (Admin only)
 * @param {number} userId - User ID
 * @param {number} points - Number of points to add
 * @returns {Promise<Object>} Updated user data
 */
export const addUserPoints = async (userId, points) => {
  return client.put(`/users/${userId}/points/add?points=${points}`).then(res => res.data);
};

/**
 * Spend points from user (Admin only)
 * @param {number} userId - User ID
 * @param {number} points - Number of points to spend
 * @returns {Promise<Object>} Updated user data
 */
export const spendUserPoints = async (userId, points) => {
  return client.put(`/users/${userId}/points/spend?points=${points}`).then(res => res.data);
};
